import { useCallback, useEffect, useRef, useState } from "react";

import { DEPLOY_PHASES, initialProgress } from "../deploy/progress.js";

import type { DeploymentProgress } from "../deploy/progress.js";
import type { TevmConfig } from "../runtime/types.js";

export interface DeploymentProgressState {
  progress: DeploymentProgress;
  isDeploying: boolean;
  isComplete: boolean;
  error: string | null;
  /** Live elapsed time in ms, updated every 100ms during deployment. */
  liveElapsedMs: number;
  /** Smooth progress percentage (0-100) weighted by expected contract counts. */
  smoothPercent: number;
  startDeployment: (config: TevmConfig) => void;
  updateProgress: (progress: DeploymentProgress) => void;
  reset: () => void;
}

/**
 * State machine hook for tracking deployment progress.
 * Adds live elapsed timer and smooth percentage interpolation.
 */
export function useDeploymentProgress(): DeploymentProgressState {
  const [progress, setProgress] = useState<DeploymentProgress>(
    initialProgress(false),
  );
  const startTimeRef = useRef(0);
  const [liveElapsedMs, setLiveElapsedMs] = useState(0);

  const isDeploying =
    progress.phase !== "idle" &&
    progress.phase !== "complete" &&
    progress.phase !== "error";

  const isComplete = progress.phase === "complete";
  const error = progress.phase === "error" ? (progress.error ?? null) : null;

  // Live elapsed timer — 100ms interval while deploying
  useEffect(() => {
    if (!isDeploying) return;
    const id = setInterval(() => {
      if (startTimeRef.current > 0) {
        setLiveElapsedMs(Date.now() - startTimeRef.current);
      }
    }, 100);
    return () => {
      clearInterval(id);
    };
  }, [isDeploying]);

  // Compute smooth percentage weighted by expected contracts per phase
  const smoothPercent = (() => {
    if (progress.phase === "complete") return 100;
    if (progress.phase === "idle" || progress.phase === "error") return 0;

    const phases = DEPLOY_PHASES;
    const totalWeight = phases.reduce(
      (s, p) => s + Math.max(p.expectedContracts, 1),
      0,
    );

    let completedWeight = 0;
    for (let i = 0; i < progress.phaseIndex; i++) {
      const p = phases[i];
      if (p) completedWeight += Math.max(p.expectedContracts, 1);
    }

    // Add partial weight for current phase (assume ~50% through)
    const currentPhase = phases[progress.phaseIndex];
    if (currentPhase) {
      completedWeight += Math.max(currentPhase.expectedContracts, 1) * 0.5;
    }

    return Math.min(99, Math.round((completedWeight / totalWeight) * 100));
  })();

  const startDeployment = useCallback((config: TevmConfig) => {
    const isFork = config.mode !== "from-scratch";
    startTimeRef.current = Date.now();
    setLiveElapsedMs(0);
    setProgress(initialProgress(isFork));
  }, []);

  const updateProgress = useCallback((p: DeploymentProgress) => {
    setProgress(p);
  }, []);

  const reset = useCallback(() => {
    startTimeRef.current = 0;
    setLiveElapsedMs(0);
    setProgress(initialProgress(false));
  }, []);

  return {
    progress,
    isDeploying,
    isComplete,
    error,
    liveElapsedMs,
    smoothPercent,
    startDeployment,
    updateProgress,
    reset,
  };
}
